import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminSecurity = ({ token }) => {
  const headers = { Authorization: `Bearer ${token}` };
  const [requests, setRequests] = useState([]);
  const [backups, setBackups] = useState([]);
  const [offsite, setOffsite] = useState([]);
  const [busy, setBusy] = useState("");
  const load = () => Promise.all([axios.get(`${API}/admin/gdpr/requests`, { headers }), axios.get(`${API}/admin/backups`, { headers }), axios.get(`${API}/admin/backups/offsite`, { headers })]).then(([a, b, c]) => { setRequests(a.data); setBackups(b.data); setOffsite(c.data); }).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (label, fn) => {
    setBusy(label);
    try { const r = await fn(); toast.success(label + " : OK"); load(); return r; }
    catch (e) { toast.error(e.response?.data?.detail || "Action impossible"); }
    finally { setBusy(""); }
  };
  const approve = (r) => window.confirm(`Supprimer définitivement les données de ${r.owner_email} (factures archivées 10 ans) ?`) && act("Suppression RGPD", () => axios.post(`${API}/admin/gdpr/requests/${r.id}/approve`, {}, { headers }));
  const reject = (r) => act("Demande refusée", () => axios.post(`${API}/admin/gdpr/requests/${r.id}/reject`, {}, { headers }));

  const box = "rounded-2xl border border-slate-800 bg-slate-900/60 p-5";
  return (
    <div className="mt-6 grid gap-4">
      <div data-testid="admin-gdpr" className={box}>
        <h3 className="font-heading text-lg font-semibold">Demandes RGPD ({requests.filter((r) => r.status === "pending").length} en attente)</h3>
        <p className="mt-1 text-xs text-slate-400">Délai légal : 30 jours. La suppression efface clients, dossiers, photos, devis non signés et conversations ; factures et pièces comptables restent archivées 10 ans.</p>
        <ul className="mt-3 divide-y divide-slate-800 text-sm">
          {requests.length === 0 && <li className="py-2 text-slate-400">Aucune demande.</li>}
          {requests.map((r) => (
            <li key={r.id} data-testid={`gdpr-request-${r.id}`} className="flex flex-wrap items-center justify-between gap-2 py-2">
              <span><span className="font-semibold">{r.owner_email}</span> · {r.status === "pending" ? `à traiter avant le ${r.due_at.slice(0, 10)}` : r.status} {r.reason ? `· « ${r.reason} »` : ""}</span>
              {r.status === "pending" && (
                <span className="flex gap-2">
                  <button type="button" data-testid={`gdpr-approve-${r.id}`} disabled={!!busy} onClick={() => approve(r)} className="rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-500 disabled:opacity-50">Supprimer les données</button>
                  <button type="button" data-testid={`gdpr-reject-${r.id}`} disabled={!!busy} onClick={() => reject(r)} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-50">Refuser</button>
                </span>
              )}
            </li>
          ))}
        </ul>
      </div>
      <div data-testid="admin-backups" className={box}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-lg font-semibold">Sauvegardes chiffrées (quotidiennes, 30 jours)</h3>
          <span className="flex gap-2">
            <button type="button" data-testid="backup-run" disabled={!!busy} onClick={() => act("Sauvegarde", () => axios.post(`${API}/admin/backups/run`, {}, { headers }))} className="rounded-lg bg-[#3A5CC8] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#2F4CA8] disabled:opacity-50">Sauvegarder maintenant</button>
            <button type="button" data-testid="backup-verify" disabled={!!busy} onClick={() => act("Test de restauration", () => axios.post(`${API}/admin/backups/verify-latest`, {}, { headers }))} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-50">Tester la dernière</button>
          </span>
        </div>
        <ul className="mt-3 divide-y divide-slate-800 text-sm">
          {backups.length === 0 && <li className="py-2 text-slate-400">Aucune sauvegarde pour l'instant (la première part 2 minutes après le démarrage).</li>}
          {backups.slice(0, 10).map((b, i) => (
            <li key={i} className="flex flex-wrap justify-between gap-2 py-2">
              <span>{new Date(b.created_at).toLocaleString("fr-BE")} · {b.status === "ok" ? `${Math.round((b.size || 0) / 1024)} Ko · ${b.trigger}` : `erreur : ${b.error}`}</span>
              {b.verified_at && <span className="text-xs text-emerald-400">restauration testée le {new Date(b.verified_at).toLocaleDateString("fr-BE")}</span>}
            </li>
          ))}
        </ul>
      </div>
      <div data-testid="admin-offsite" className={box}>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <h3 className="font-heading text-lg font-semibold">Copie hors site hebdomadaire (coffre « vault », 90 jours)</h3>
          <button type="button" data-testid="offsite-run" disabled={!!busy} onClick={() => act("Copie hors site", () => axios.post(`${API}/admin/backups/offsite/run`, {}, { headers }))} className="rounded-lg border border-slate-700 px-3 py-1.5 text-xs font-semibold hover:bg-slate-800 disabled:opacity-50">Copier maintenant</button>
        </div>
        <p className="mt-1 text-xs text-slate-400">Chaque semaine, la dernière sauvegarde est relue, déchiffrée pour vérifier son intégrité, puis recopiée dans un emplacement de stockage distinct.</p>
        <ul className="mt-3 divide-y divide-slate-800 text-sm">
          {offsite.length === 0 && <li className="py-2 text-slate-400">Aucune copie hors site pour l'instant.</li>}
          {offsite.slice(0, 8).map((b, i) => (
            <li key={i} data-testid={`offsite-row-${i}`} className="flex flex-wrap justify-between gap-2 py-2">
              <span>{new Date(b.created_at).toLocaleString("fr-BE")} · {b.status === "ok" ? `${Math.round((b.size || 0) / 1024)} Ko · ${b.documents} documents · ${b.trigger}` : `erreur : ${b.error}`}</span>
              {b.status === "ok" && <span className="text-xs text-emerald-400">intégrité vérifiée</span>}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
};
